// Turn a task title into a safe file name: strip characters that are invalid
// on Windows/macOS/Linux, collapse whitespace and cap the length.
export function toMarkdownFileName(title: string, fallback = 'note'): string {
  const base = title
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f<>:"/\\|?*]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\.+|\.+$/g, '')
    .slice(0, 120)
    .trim();
  return `${base || fallback}.md`;
}

interface FileKind {
  description: string;
  mime: string;
  extension: string;
}

const MARKDOWN: FileKind = { description: 'Markdown', mime: 'text/markdown', extension: '.md' };
const ZIP: FileKind = { description: 'ZIP', mime: 'application/zip', extension: '.zip' };

export async function downloadMarkdown(fileName: string, content: string): Promise<void> {
  await saveFile(fileName, MARKDOWN, () => new Blob([content], { type: 'text/markdown;charset=utf-8' }));
}

// Pack several Markdown notes into one ZIP. Duplicate names get a " (2)",
// " (3)", … suffix so no note overwrites another inside the archive.
export async function downloadMarkdownZip(
  zipName: string,
  notes: { title: string; content: string }[]
): Promise<void> {
  await saveFile(zipName, ZIP, async () => {
    const { zipSync, strToU8 } = await import('fflate');
    const files: Record<string, Uint8Array> = {};
    for (const note of notes) {
      const fileName = toMarkdownFileName(note.title);
      const stem = fileName.slice(0, -'.md'.length);
      let name = fileName;
      for (let i = 2; name in files; i++) name = `${stem} (${i}).md`;
      files[name] = strToU8(note.content);
    }
    return new Blob([zipSync(files, { level: 6 })], { type: 'application/zip' });
  });
}

type SaveFilePicker = (options: {
  suggestedName?: string;
  types?: { description: string; accept: Record<string, string[]> }[];
}) => Promise<{ createWritable: () => Promise<{ write: (data: Blob) => Promise<void>; close: () => Promise<void> }> }>;

// Ask the user where to save via the File System Access API (Chrome, Edge,
// Opera). Browsers without it (Safari, Firefox) fall back to a regular
// download, which goes wherever the browser's download settings say.
// The picker is opened BEFORE the blob is built so it stays within the
// click's user activation; cancelling the dialog is a silent no-op.
async function saveFile(fileName: string, kind: FileKind, buildBlob: () => Blob | Promise<Blob>): Promise<void> {
  const showSaveFilePicker = (window as unknown as { showSaveFilePicker?: SaveFilePicker }).showSaveFilePicker;
  if (!showSaveFilePicker) {
    downloadBlob(fileName, await buildBlob());
    return;
  }

  let handle: Awaited<ReturnType<SaveFilePicker>>;
  try {
    handle = await showSaveFilePicker({
      suggestedName: fileName,
      types: [{ description: kind.description, accept: { [kind.mime]: [kind.extension] } }],
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return; // user cancelled
    // Picker blocked (e.g. inside an iframe) — fall back to a plain download.
    downloadBlob(fileName, await buildBlob());
    return;
  }

  const writable = await handle.createWritable();
  await writable.write(await buildBlob());
  await writable.close();
}

function downloadBlob(fileName: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoke on the next tick so the browser has started the download.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
